// frontend/src/pages/MatchScoring/commentary.js
//
// Pure function: given a delivery + zone/shot/fielder/incident,
// returns a one-line commentary string.
//
// Mirrors the rules from the mockup:
//   - Verb depends on delivery kind + runs
//   - Shot phrasing only used for ball-off-bat
//   - Zone label is the ring-specific name (inner/circle/boundary)
//   - Fielder appended if any
//   - Incident appended at end

// Push 3 v3 FINAL (Session 21 walkthrough — 26 May 2026):
// 9 angular zones (40° each) — adds Mid-On to the original 8. Long-on /
// Long-off / Cow Corner / Bat-Pad etc. are NOT separate slices; they emerge
// from the (zone, depth) pair via context-aware position chips that the
// modal renders below the wheel after a slice is tapped.
//
// `c` = center angle in degrees; 0° = towards bowler. Clockwise → off side first.
// Slice width = 40° (20° half-width on each side of c).
//
// Each zone exposes three depth-named positions for its chip row:
//   - inner    = close-in / silly / slip / bat-pad   → maps to ring 'inner'
//   - circle   = the standard inner-ring position    → maps to ring 'circle'
//   - boundary = the deep / long / cow-corner equiv  → maps to ring 'boundary'
// A '—' marks a depth as DISABLED for that zone.
//
// `extras` (optional) is a list of additional named position chips for the
// slice. Each entry has { label, ring }. Used for Straight, where the
// scorer wants to distinguish "Long Off" from "Long On" but both share
// (zone=Straight, ring=boundary). The wagon-wheel viz can later render
// the label correctly using a stored secondary hint (we encode it in
// shot_type or a separate field — TBD when viz is built).
export const SECTORS = [
  { c:   0, side: 'mid', circle: 'Straight',   inner: '—',             boundary: 'Straight Boundary',
    extras: [
      { label: 'Long Off', ring: 'boundary', sideHint: 'off' },
      { label: 'Long On',  ring: 'boundary', sideHint: 'leg' },
    ] },
  { c:  40, side: 'off', circle: 'Mid-Off',    inner: 'Silly Mid-Off', boundary: 'Long Off',
    extras: [
      { label: 'Long Off',      ring: 'boundary', sideHint: 'straight' },
      { label: 'Wide Long-off', ring: 'boundary', sideHint: 'wide' },
    ] },
  { c:  80, side: 'off', circle: 'Cover',      inner: 'Silly Point',   boundary: 'Deep Cover',
    extras: [
      { label: 'Deep Cover',       ring: 'boundary', sideHint: 'square' },
      { label: 'Deep Extra Cover', ring: 'boundary', sideHint: 'straight' },
    ] },
  { c: 120, side: 'off', circle: 'Point',      inner: 'Gully',         boundary: 'Deep Point',
    extras: [
      { label: 'Deep Point',          ring: 'boundary', sideHint: 'square' },
      { label: 'Deep Backward Point', ring: 'boundary', sideHint: 'backward' },
    ] },
  { c: 160, side: 'off', circle: 'Third Man',  inner: 'Slip',          boundary: 'Deep Third Man',
    extras: [
      { label: 'Third Man',      ring: 'boundary', sideHint: 'square' },
      { label: 'Deep Third Man', ring: 'boundary', sideHint: 'fine' },
    ] },
  { c: 200, side: 'leg', circle: 'Fine Leg',   inner: 'Leg Slip',      boundary: 'Deep Fine Leg',
    extras: [
      { label: 'Deep Fine Leg', ring: 'boundary', sideHint: 'fine' },
      { label: 'Long Leg',      ring: 'boundary', sideHint: 'square' },
    ] },
  { c: 240, side: 'leg', circle: 'Square Leg', inner: 'Short Leg',     boundary: 'Deep Square Leg',
    extras: [
      { label: 'Deep Square Leg',          ring: 'boundary', sideHint: 'square' },
      { label: 'Deep Backward Square Leg', ring: 'boundary', sideHint: 'backward' },
    ] },
  { c: 280, side: 'leg', circle: 'Midwicket',  inner: 'Bat-Pad',       boundary: 'Cow Corner',
    extras: [
      { label: 'Deep Midwicket', ring: 'boundary', sideHint: 'square' },
      { label: 'Cow Corner',     ring: 'boundary', sideHint: 'wide' },
    ] },
  { c: 320, side: 'leg', circle: 'Mid-On',     inner: 'Silly Mid-On',  boundary: 'Long On',
    extras: [
      { label: 'Long On',      ring: 'boundary', sideHint: 'straight' },
      { label: 'Wide Long-on', ring: 'boundary', sideHint: 'wide' },
    ] },
];

// Default shot suggestions per wheel slice. Used by the wagon-wheel modal
// to show only 3-4 contextually-relevant shot chips when a slice is tapped.
// Scorer can hit "+ other" to expand to the full SHOT_TYPES list if they
// played something unusual. Keys MUST match SHOT_VERBS keys.
export const SHOTS_BY_SECTOR = [
  ['drive', 'lofted', 'punch', 'defence'],                     // 0  Straight
  ['drive', 'lofted', 'punch', 'backfoot_punch', 'defence'],   // 1  Mid-Off
  ['drive', 'cut', 'square_drive', 'punch', 'backfoot_punch', 'defence'], // 2  Cover
  ['cut', 'square_drive', 'late_cut', 'backfoot_punch', 'defence'],       // 3  Point
  ['late_cut', 'edge', 'ramp', 'reverse_sweep'],               // 4  Third Man
  ['flick', 'glance', 'edge', 'ramp'],                         // 5  Fine Leg
  ['flick', 'pull', 'sweep', 'glance'],                        // 6  Square Leg
  ['flick', 'pull', 'sweep', 'slog', 'drive'],                 // 7  Midwicket
  ['flick', 'drive', 'lofted', 'defence'],                     // 8  Mid-On
];

// Bug #20 fix (Session 21 walkthrough — 26 May 2026): added verbs for the
// 4 new shot types — reverse_sweep, slog, ramp, late_cut.
const SHOT_VERBS = {
  drive: 'drives', cut: 'cuts', pull: 'pulls', sweep: 'sweeps',
  glance: 'glances', defence: 'defends', lofted: 'lofts', edge: 'edges',
  reverse_sweep: 'reverse-sweeps', slog: 'slogs', ramp: 'ramps', late_cut: 'late-cuts',
  flick: 'flicks',
  punch: 'punches', backfoot_punch: 'punches off the back foot',
  frontfoot_punch: 'punches off the front foot',
  square_drive: 'square-drives'
};

// Built-in master shot list (used by the wheel's "+ other" expansion). The
// admin-managed custom shots (from the DB) are MERGED on top of this.
export const SHOT_TYPES_DEFAULT = [
  { key: 'drive',          label: 'Drive' },
  { key: 'cut',            label: 'Cut' },
  { key: 'pull',           label: 'Pull' },
  { key: 'sweep',          label: 'Sweep' },
  { key: 'glance',         label: 'Glance' },
  { key: 'flick',          label: 'Flick' },
  { key: 'defence',        label: 'Defence' },
  { key: 'lofted',         label: 'Lofted' },
  { key: 'edge',           label: 'Edge' },
  { key: 'reverse_sweep',  label: 'Reverse Sweep' },
  { key: 'slog',           label: 'Slog' },
  { key: 'ramp',           label: 'Ramp/Scoop' },
  { key: 'late_cut',       label: 'Late Cut' },
  { key: 'square_drive',   label: 'Square Drive' },
  { key: 'punch',          label: 'Punch' },
  { key: 'backfoot_punch', label: 'Backfoot Punch' },
  { key: 'frontfoot_punch',label: 'Frontfoot Punch' },
];

// ── Admin-managed config overlay ───────────────────────────────────
// Populated once at runtime via applyWheelConfig() (the scoring console
// fetches GET /scorer/wheel-config). Defaults above always remain, so an
// empty or failed config never breaks the wheel.
let _extraShots = [];      // [{ key, label, verb, sectors:[int] }]
let _extraPositions = [];  // [{ sector, ring, label, sideHint }]

export function applyWheelConfig(cfg) {
  _extraShots     = Array.isArray(cfg?.shots)     ? cfg.shots     : [];
  _extraPositions = Array.isArray(cfg?.positions) ? cfg.positions : [];
}

// Full shot list = built-ins + custom (deduped by key).
export function shotTypeList() {
  const seen = new Set(SHOT_TYPES_DEFAULT.map(s => s.key));
  const extra = _extraShots
    .filter(s => s.key && s.label && !seen.has(s.key))
    .map(s => ({ key: s.key, label: s.label }));
  return [...SHOT_TYPES_DEFAULT, ...extra];
}

// Default chips for a wheel slice = built-ins + custom shots that list this
// sector (deduped, in order).
export function shotsForSector(sector) {
  const base = SHOTS_BY_SECTOR[sector] || [];
  const extra = _extraShots
    .filter(s => s.key && Array.isArray(s.sectors) && s.sectors.includes(sector))
    .map(s => s.key)
    .filter(k => !base.includes(k));
  return [...base, ...extra];
}

// POSITION chips for a slice = built-in inner/circle/boundary/extras +
// any custom positions added for that sector. Returns [{label, ring, sideHint}].
export function sectorChips(sector) {
  const sec = SECTORS[sector];
  if (!sec) return [];
  const chips = [];
  if (sec.inner  && sec.inner  !== '—') chips.push({ label: sec.inner,  ring: 'inner',  sideHint: null });
  if (sec.circle && sec.circle !== '—') chips.push({ label: sec.circle, ring: 'circle', sideHint: null });
  if (sec.extras && sec.extras.length > 0) {
    sec.extras.forEach(ex => chips.push({ label: ex.label, ring: ex.ring, sideHint: ex.sideHint }));
  } else if (sec.boundary && sec.boundary !== '—') {
    chips.push({ label: sec.boundary, ring: 'boundary', sideHint: null });
  }
  _extraPositions
    .filter(p => Number(p.sector) === sector && p.label)
    .forEach(p => chips.push({ label: p.label, ring: p.ring || 'boundary', sideHint: p.sideHint || null }));
  return chips;
}

// Resolve a shot's commentary verb: built-in first, then custom, else "plays".
function resolveShotVerb(key) {
  if (SHOT_VERBS[key]) return SHOT_VERBS[key];
  const c = _extraShots.find(s => s.key === key);
  return (c && c.verb) ? c.verb : 'plays';
}

export function zoneLabel(sector, ring, sideHint = null) {
  if (sector == null) return null;
  const s = SECTORS[sector];
  if (!s) return null;
  // Push 3 v3 (Session 21 — 26 May 2026): if the sector has `extras`
  // (currently only Straight) and a sideHint is provided, return the
  // extras entry whose sideHint matches. This is how Straight + boundary
  // becomes "Long Off" vs "Long On" instead of generic "Straight Boundary".
  if (sideHint && s.extras && s.extras.length > 0 && ring === 'boundary') {
    const match = s.extras.find(ex => ex.ring === 'boundary' && ex.sideHint === sideHint);
    if (match) return match.label;
  }
  // Custom (admin-added) position chips: match on sector + ring + sideHint.
  if (sideHint) {
    const cp = _extraPositions.find(p =>
      Number(p.sector) === sector && (p.ring || 'boundary') === ring && (p.sideHint || null) === sideHint);
    if (cp) return cp.label;
  }
  // Push 3: any depth may be disabled (marked '—'). Fall back to the
  // boundary (deep) label when circle/inner are disabled — this is the
  // case for Long-on and Long-off which are inherently deep.
  const safe = (val) => (val && val !== '—') ? val : null;
  if (ring === 'inner')    return safe(s.inner)    || safe(s.circle) || safe(s.boundary);
  if (ring === 'boundary') return safe(s.boundary) || safe(s.circle) || safe(s.inner);
  return safe(s.circle) || safe(s.boundary) || safe(s.inner);
}

export function composeCommentary({
  kind = 'bat',                  // 'bat' | 'wd' | 'nb' | 'b' | 'lb' | 'wkt'
  runs = 0,
  offTheBat = 0,
  zoneSector = null,
  zoneRing = 'circle',
  zoneSideHint = null,           // Push 3 v3: 'off'|'leg'|null — disambiguates Straight + boundary
  shotType = null,
  fielderName = null,
  incident = null,               // 'drop' | 'missed_runout' | 'misfield' | null
  batterName = '',
  bowlerName = '',
  dismissalType = null,          // 'bowled' | 'caught' | 'lbw' | ...
} = {}) {
  // Verb
  let verb;
  if (dismissalType) {
    const dis = dismissalType.replace(/_/g, ' ').toUpperCase();
    verb = `OUT — ${dis}!`;
  } else if (kind === 'wd')          verb = 'WIDE!';
  else if (kind === 'nb' && offTheBat === 4) verb = 'FOUR! (off no-ball)';
  else if (kind === 'nb' && offTheBat === 6) verb = 'SIX! (off no-ball)';
  else if (kind === 'nb' && offTheBat > 0)   verb = `${offTheBat} off no-ball.`;
  else if (kind === 'nb')            verb = 'NO-BALL.';
  else if (kind === 'b')             verb = `${runs} bye${runs === 1 ? '' : 's'}.`;
  else if (kind === 'lb')            verb = `${runs} leg-bye${runs === 1 ? '' : 's'}.`;
  else if (runs === 4)               verb = 'FOUR!';
  else if (runs === 6)               verb = 'SIX!';
  else if (runs === 0)               verb = 'Dot ball.';
  else                               verb = `${runs} run${runs === 1 ? '' : 's'}.`;

  const useShotVerb = (kind === 'bat') || (kind === 'nb' && offTheBat > 0);
  const shotVerb = useShotVerb ? resolveShotVerb(shotType) : 'goes';

  const zone = zoneLabel(zoneSector, zoneRing, zoneSideHint);
  const zoneText = zone ? zone.toLowerCase() : null;

  let line = verb;
  if (batterName) line += ` ${batterName}`;
  if (zoneText && (kind === 'bat' || kind === 'nb' || kind === 'b' || kind === 'lb')) {
    line += ` ${shotVerb} through ${zoneText}`;
  }
  if (fielderName) {
    const dt = (dismissalType || '').toLowerCase().replace(/_/g, ' ');
    if (dt === 'caught behind') {
      line += `, caught by wicket keeper ${fielderName}`;
    } else if (dt === 'caught' || dt === 'caught and bowled') {
      line += `, caught by ${fielderName}`;
      if (dt === 'caught' && zoneText) line += ` at ${zoneText}`;
    } else if (dt === 'run out') {
      line += `, run out by ${fielderName}`;
    } else if (dt === 'stumped') {
      line += `, stumped by ${fielderName}`;
    } else if (incident !== 'drop') {
      line += `, fielded by ${fielderName}`;
    }
  }
  if (bowlerName) line += ` off ${bowlerName}`;
  line += '.';

  if (incident === 'drop') {
    line += fielderName ? ` Catch dropped by ${fielderName}!` : ' Catch dropped!';
  } else if (incident) {
    const inc = {
      missed_runout: ' Run-out chance goes begging.',
      misfield: ' Sloppy fielding.',
    }[incident];
    if (inc) line += inc;
  }

  // No-ball ALWAYS triggers a free-hit. Append a hint so player-facing
  // commentary clearly signals it.
  if (kind === 'nb' && !dismissalType) {
    line += ' Free hit next ball.';
  }
  return line;
}
